import { api } from './api.js';
import { Tracker } from './tracker.js';
import * as UI from './ui.js';

const root = document.getElementById('app');
let tracker = null;
let activeRender = null;
let installPromptEvent = null;

const token = extractToken();

window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  installPromptEvent = e;
});

if ('serviceWorker' in navigator) {
  navigator.serviceWorker.register('/sw.js').catch((e) => console.warn('sw register failed', e));
}

boot();

async function boot() {
  if (!token) {
    UI.renderError(root, { title: 'No trip', message: 'This URL does not include a trip token.' });
    return;
  }
  UI.renderLoading(root);

  if (!('wakeLock' in navigator)) {
    UI.renderInAppWarning(root, location.href);
    return;
  }

  let trip;
  try {
    trip = await api.getTrip(token);
  } catch (e) {
    if (e.status === 404) UI.renderError(root, { title: 'Trip not found', message: 'This trip link is invalid or expired.' });
    else UI.renderError(root, { title: 'Could not load trip', message: 'Check your connection and try again.', action: { label: 'Retry', onClick: boot } });
    return;
  }

  if (trip.status === 'ended' || trip.status === 'cancelled') {
    UI.renderEnded(root, { canInstall: !!installPromptEvent, onInstall: tryInstall });
    return;
  }

  const persisted = readState();
  if (trip.status === 'active' || persisted === 'ACTIVE') {
    await goActive(trip);
    return;
  }
  UI.renderIdle(root, { label: trip.label, onStart: () => goStart(trip) });
}

async function goStart(trip) {
  UI.renderStarting(root);
  let fix;
  try {
    fix = await firstFix();
  } catch (e) {
    if (e.code === 1) UI.renderPermissionDenied(root, { onRetry: () => location.reload() });
    else UI.renderError(root, { title: 'Could not get GPS', message: 'Check that location is enabled and try again.', action: { label: 'Retry', onClick: () => location.reload() } });
    return;
  }

  if ('Notification' in window && Notification.permission === 'default') {
    try { await Notification.requestPermission(); } catch {}
  }

  try {
    await api.start(token, {
      lat: fix.coords.latitude,
      lng: fix.coords.longitude,
      accuracy: fix.coords.accuracy,
      captured_at: fix.timestamp,
    });
  } catch (e) {
    UI.renderError(root, { title: 'Could not start trip', message: 'Server rejected the start. Check your connection.', action: { label: 'Retry', onClick: () => location.reload() } });
    return;
  }

  writeState('ACTIVE');
  await goActive(trip);
}

async function goActive(trip) {
  activeRender = UI.renderActive(root, { label: trip.label, onEnd: () => goEnd(trip) });

  tracker = new Tracker(token, {
    onFix: (p) => {
      activeRender.setAccuracy(p.coords.accuracy);
      activeRender.setLastFix(p.timestamp);
      activeRender.clearError();
    },
    onQueueChange: (n) => activeRender.setQueue(n),
    onResumeAfterHidden: (ms) => activeRender.showHiddenWarning(ms),
    onPermissionLost: () => UI.renderPermissionDenied(root, { onRetry: () => location.reload() }),
    onPositionError: () => activeRender.showError('Waiting for GPS signal…'),
    onSyncError: () => activeRender.showError('Network slow — pings are buffering locally.'),
    onWakeLockError: () => activeRender.showError('Could not keep screen on — please disable auto-lock manually.'),
  });

  try { await tracker.start(); }
  catch (e) { console.error('tracker start failed', e); }
}

async function goEnd(trip) {
  UI.renderEnding(root);
  let fix = tracker?.getLastFix();
  try { fix = await firstFix(); } catch { /* use last */ }

  try {
    if (tracker) await tracker.finalFlush();
    await api.end(token, fix ? {
      lat: fix.coords.latitude,
      lng: fix.coords.longitude,
      accuracy: fix.coords.accuracy,
      captured_at: fix.timestamp,
    } : {});
  } catch (e) {
    console.error('end failed', e);
  } finally {
    if (tracker) await tracker.stop();
    tracker = null;
    clearState();
  }

  UI.renderEnded(root, { canInstall: !!installPromptEvent, onInstall: tryInstall });
}

function firstFix() {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      resolve,
      (err) => {
        if (err.code === 3 || err.code === 2) {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: false, timeout: 20000, maximumAge: 30000,
          });
        } else {
          reject(err);
        }
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 },
    );
  });
}

function extractToken() {
  const m = location.pathname.match(/^\/t\/([^/?#]+)/);
  if (m) return decodeURIComponent(m[1]);
  const q = new URLSearchParams(location.search).get('token');
  return q || null;
}

function stateKey() { return `trip:${token}:state`; }
function readState() { try { return localStorage.getItem(stateKey()); } catch { return null; } }
function writeState(v) { try { localStorage.setItem(stateKey(), v); } catch {} }
function clearState() { try { localStorage.removeItem(stateKey()); } catch {} }

async function tryInstall() {
  if (!installPromptEvent) return;
  installPromptEvent.prompt();
  await installPromptEvent.userChoice;
  installPromptEvent = null;
}
